import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { resignationAPI } from "../../lib/api";
import { toast } from "sonner";
import { formatDate, capitalizeFirst, getStatusColor } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { UserMinus, Plus, Loader2, CheckCircle, Eye } from "lucide-react";

const ResignationsPage = () => {
  const { user, isEmployee, isManager, isHRAdmin } = useAuth();
  const [resignations, setResignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [viewResignation, setViewResignation] = useState(null);
  const [formData, setFormData] = useState({
    reason: "",
    last_working_date: "",
    notice_period_days: 30,
  });

  useEffect(() => {
    fetchResignations();
  }, []);

  const fetchResignations = async () => {
    try {
      const response = await resignationAPI.getAll();
      setResignations(response.data);
    } catch (error) {
      toast.error("Failed to fetch resignations");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      await resignationAPI.submit(formData);
      toast.success("Resignation submitted");
      setShowSubmitDialog(false);
      setFormData({ reason: "", last_working_date: "", notice_period_days: 30 });
      fetchResignations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit resignation");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleApprove = async (resignationId) => {
    try {
      await resignationAPI.approve(resignationId);
      toast.success("Resignation approved");
      fetchResignations();
    } catch (error) {
      toast.error("Failed to approve resignation");
    }
  };

  const handleComplete = async (resignationId) => {
    try {
      await resignationAPI.complete(resignationId);
      toast.success("Exit process completed");
      fetchResignations();
    } catch (error) {
      toast.error("Failed to complete exit process");
    }
  };

  const handleChecklistUpdate = async (resignationId, itemIndex, completed) => {
    try {
      await resignationAPI.updateChecklist(resignationId, itemIndex, completed);
      toast.success("Checklist updated");
      fetchResignations();
      if (viewResignation) {
        const updated = resignations.find((r) => r.id === resignationId);
        if (updated) {
          const newChecklist = [...updated.exit_checklist];
          newChecklist[itemIndex].completed = completed;
          setViewResignation({ ...updated, exit_checklist: newChecklist });
        }
      }
    } catch (error) {
      toast.error("Failed to update checklist");
    }
  };

  const canApprove = isManager || isHRAdmin;
  const myResignation = resignations.find((r) => r.user_id === user?.id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="resignations-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Resignations</h1>
          <p className="text-slate-500">Manage resignation requests and exit process</p>
        </div>
        {isEmployee && !myResignation && (
          <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" data-testid="submit-resignation-btn">
                <Plus className="w-4 h-4 mr-2" />
                Submit Resignation
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">Submit Resignation</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="form-group">
                  <Label>Reason for Resignation</Label>
                  <Textarea
                    placeholder="Please provide your reason..."
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    required
                    rows={4}
                    data-testid="resignation-reason"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <Label>Last Working Date</Label>
                    <Input
                      type="date"
                      value={formData.last_working_date}
                      onChange={(e) => setFormData({ ...formData, last_working_date: e.target.value })}
                      required
                      data-testid="resignation-lwd"
                    />
                  </div>
                  <div className="form-group">
                    <Label>Notice Period (days)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.notice_period_days}
                      onChange={(e) => setFormData({ ...formData, notice_period_days: parseInt(e.target.value) })}
                      required
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={submitLoading} data-testid="confirm-resignation">
                  {submitLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Resignation"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Resignations Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Submitted On</TableHead>
                <TableHead>Last Working Date</TableHead>
                <TableHead>Notice Period</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {resignations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    No resignations found
                  </TableCell>
                </TableRow>
              ) : (
                resignations.map((resignation) => (
                  <TableRow key={resignation.id} data-testid={`resignation-row-${resignation.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                          <UserMinus className="w-5 h-5 text-red-600" />
                        </div>
                        <span className="font-medium">
                          {resignation.user_id === user?.id ? "You" : "Employee"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>{formatDate(resignation.submitted_at)}</TableCell>
                    <TableCell>{formatDate(resignation.last_working_date)}</TableCell>
                    <TableCell>{resignation.notice_period_days} days</TableCell>
                    <TableCell>
                      <Badge className={`badge badge-${getStatusColor(resignation.status)}`}>
                        {capitalizeFirst(resignation.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setViewResignation(resignation)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {canApprove && resignation.status === "submitted" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-emerald-600"
                            onClick={() => handleApprove(resignation.id)}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                        {isHRAdmin && resignation.status === "hr_approved" && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleComplete(resignation.id)}
                          >
                            Complete
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* View Resignation Dialog */}
      <Dialog open={!!viewResignation} onOpenChange={() => setViewResignation(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Resignation Details</DialogTitle>
          </DialogHeader>
          {viewResignation && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm text-slate-500">Submitted On</p>
                  <p className="font-medium">{formatDate(viewResignation.submitted_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Last Working Date</p>
                  <p className="font-medium">{formatDate(viewResignation.last_working_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Notice Period</p>
                  <p className="font-medium">{viewResignation.notice_period_days} days</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Status</p>
                  <Badge className={`badge badge-${getStatusColor(viewResignation.status)}`}>
                    {capitalizeFirst(viewResignation.status)}
                  </Badge>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2">Reason</h4>
                <p className="text-slate-600 text-sm">{viewResignation.reason}</p>
              </div>

              {isHRAdmin && (
                <div>
                  <h4 className="font-medium mb-3">Exit Checklist</h4>
                  <div className="space-y-2">
                    {viewResignation.exit_checklist?.map((item, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Checkbox
                          id={`checklist-${index}`}
                          checked={item.completed}
                          onCheckedChange={(checked) =>
                            handleChecklistUpdate(viewResignation.id, index, checked)
                          }
                        />
                        <Label
                          htmlFor={`checklist-${index}`}
                          className={`text-sm ${item.completed ? "line-through text-slate-400" : ""}`}
                        >
                          {item.item}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResignationsPage;
